import { JsonReaderService } from 'src/app/services/json-reader/json-reader.service';
import { CraftGroup, DoneCraft } from './craft-models';
import { AoDataGetterService } from 'src/app/services/ao-data-getter/ao-data-getter.service';
import { Location } from '../location.model';
import { Signal } from '@angular/core';
import { Achievement } from '../achievement.model';
import { PriceType } from '../market.model';
import { CraftCalculatorStateService } from 'src/app/pages/craft-calculator/craft-calculator-state.service';

export interface SaveCraftGroupResponse {
  message: string;
  id?: string;
  craftGroup?: CraftGroupPersistenceModel;
}

export interface DoneCraftPersistanceModel {
  id?: string;
  craftGroup: CraftGroupPersistenceModel;
  totalFocus: number;
  totalCraftFamePlayerTotal: number;
  totalCraftFee: number;
  totalExpenses: number;
  totalIncome: number;
  totalResult: number;
  craftDate?: Date;
  totalInputWeight: number;
  totalOutputWeight: number;
}

export interface CraftGroupPersistenceModel {
  id?: string;
  name: string;
  server: string;
  hasPremium?: boolean;
  hideUnselectedCrafts?: boolean;
  hideMissingPrices?: boolean;
  onlyMissingPricesCrafts?: boolean;
  hideConsumablesPicker?: boolean;
  hideFameInfo?: boolean;
  hideFocusInfo?: boolean;
  showCharts?: boolean;
  averageNumberOfDays?: number;
  hideUnusedItems?: boolean;
  onlyMissingPricesItems?: boolean;
  onlyShowMainRecipes?: boolean;
  crafts: CraftPersistenceModel[];
  userPrices: UserPriceModel[];
  updatedAt?: Date;
}

export interface CraftPersistenceModel {
  productUniqueName: string;
  recipeIndex: number;
  numberOfRuns: number;
  rrr: number;
  useFocus: boolean;
  craftLocationId?: number;
  craftFeePerNutrition: number;
  selected: boolean;
  extraExpenses: number;
  extraIncome: number;
  ignoreJournals: boolean;
  consumables: { uniqueName: string; amount: number }[];
}

export interface UserPriceModel {
  itemUniqueName: string;
  type: 'input' | 'output';
  buyUserPrice?: number; // For input items
  sellUserPrice?: number; // For both input (leftover) and output items
  buyLocationId?: number; // For input items
  buyPriceType?: string; // For input items
  leftOverSellPriceType?: string; // For input items
  sellLocationId?: number; // For output items
  sellPriceType?: string; // For output items
}

// Helper class to convert between full models and persistence models
export class CraftGroupPersistenceHelper {
  //MARK: toPersistenceModel
  static toPersistenceModel(craftGroup: CraftGroup): CraftGroupPersistenceModel {
    const crafts: CraftPersistenceModel[] = craftGroup.crafts().map((craft) => ({
      productUniqueName: craft.product.uniqueName,
      recipeIndex: craft.product.craftingRequirements.indexOf(craft.recipe),
      numberOfRuns: craft.numberOfRuns(),
      rrr: craft.rrr(),
      useFocus: craft.useFocus(),
      craftLocationId: craft.craftLocation()?.index,
      craftFeePerNutrition: craft.craftFeePerNutrition(),
      selected: craft.selected(),
      extraExpenses: craft.extraExpenses(),
      extraIncome: craft.extraIncome(),
      ignoreJournals: craft.ignoreJournals(),
      consumables: craft.consumables().map((consumable) => ({
        uniqueName: consumable.inputItem.item.uniqueName,
        amount: consumable.inputAmountPerRun(),
      })),
    }));

    const userPrices: UserPriceModel[] = [
      // Input item prices
      ...craftGroup.inputItems().map((inputItem) => ({
        itemUniqueName: inputItem.item.uniqueName,
        type: 'input' as const,
        buyUserPrice: inputItem.buyUserPrice(),
        sellUserPrice: inputItem.sellUserPrice(),
        buyLocationId: inputItem.buyLocation()?.index,
        buyPriceType: inputItem.buyPriceType()?.name,
        leftOverSellPriceType: inputItem.leftOverSellPriceType()?.name,
      })),
      // Output item prices
      ...craftGroup.outputItems().map((outputItem) => ({
        itemUniqueName: outputItem.item.uniqueName,
        type: 'output' as const,
        sellUserPrice: outputItem.sellUserPrice(),
        sellLocationId: outputItem.sellLocation()?.index,
        sellPriceType: outputItem.sellPriceType()?.name,
      })),
    ];

    return {
      id: craftGroup.id(),
      name: craftGroup.name(),
      server: craftGroup.server(),
      hasPremium: craftGroup.hasPremium(),
      hideUnselectedCrafts: craftGroup.hideUnselectedCrafts(),
      hideMissingPrices: craftGroup.hideMissingPrices(),
      onlyMissingPricesCrafts: craftGroup.onlyMissingPricesCrafts(),
      hideConsumablesPicker: craftGroup.hideConsumablesPicker(),
      hideFameInfo: craftGroup.hideFameInfo(),
      hideFocusInfo: craftGroup.hideFocusInfo(),
      showCharts: craftGroup.showCharts(),
      averageNumberOfDays: craftGroup.averageNumberOfDays(),
      hideUnusedItems: craftGroup.hideUnusedItems(),
      onlyMissingPricesItems: craftGroup.onlyMissingPricesItems(),
      onlyShowMainRecipes: craftGroup.onlyShowMainRecipes(),
      crafts: crafts,
      userPrices: userPrices,
      updatedAt: craftGroup.updatedAt(),
    };
  }

  //MARK: fromPersistenceModel
  static fromPersistenceModel(
    persistenceModel: CraftGroupPersistenceModel,
    jsonReaderService: JsonReaderService,
    aoDataGetterService: AoDataGetterService,
    achievementsSignal: Signal<Achievement[]>,
    craftCalculatorStateService: CraftCalculatorStateService,
    locations: Location[],
    priceTypes: PriceType[]
  ): CraftGroup {
    // Create new CraftGroup
    const craftGroup = new CraftGroup(jsonReaderService, aoDataGetterService, achievementsSignal, craftCalculatorStateService.userSpecsLevels);

    // Set basic properties
    craftGroup.id.set(persistenceModel.id);
    craftGroup.name.set(persistenceModel.name);
    craftGroup.server.set(persistenceModel.server);
    craftGroup.hasPremium.set(persistenceModel.hasPremium ?? false);
    craftGroup.hideUnselectedCrafts.set(persistenceModel.hideUnselectedCrafts ?? false);
    craftGroup.hideMissingPrices.set(persistenceModel.hideMissingPrices ?? false);
    craftGroup.onlyMissingPricesCrafts.set(persistenceModel.onlyMissingPricesCrafts ?? false);
    craftGroup.hideConsumablesPicker.set(persistenceModel.hideConsumablesPicker ?? false);
    craftGroup.hideFameInfo.set(persistenceModel.hideFameInfo ?? false);
    craftGroup.hideFocusInfo.set(persistenceModel.hideFocusInfo ?? false);
    craftGroup.showCharts.set(persistenceModel.showCharts ?? false);
    craftGroup.averageNumberOfDays.set(persistenceModel.averageNumberOfDays ?? 14);
    craftGroup.hideUnusedItems.set(persistenceModel.hideUnusedItems ?? false);
    craftGroup.onlyMissingPricesItems.set(persistenceModel.onlyMissingPricesItems ?? false);
    craftGroup.onlyShowMainRecipes.set(persistenceModel.onlyShowMainRecipes ?? false);
    craftGroup.updatedAt.set(persistenceModel.updatedAt ? new Date(persistenceModel.updatedAt) : new Date());

    // Recreate crafts using addCraft method
    for (const craftPersistence of persistenceModel.crafts) {
      const product = jsonReaderService.getItemFromIndex(craftPersistence.productUniqueName);
      if (product) {
        const addedCrafts = craftGroup.addCraft(product);
        if (addedCrafts) {
          // Find the specific craft by recipe index
          const craft = addedCrafts.find((c) => c.product.craftingRequirements.indexOf(c.recipe) === craftPersistence.recipeIndex);

          if (craft) {
            // Apply saved settings
            craft.numberOfRuns.set(craftPersistence.numberOfRuns);
            craft.rrr.set(craftPersistence.rrr);
            craft.useFocus.set(craftPersistence.useFocus);
            craft.craftFeePerNutrition.set(craftPersistence.craftFeePerNutrition);
            craft.selected.set(craftPersistence.selected);
            craft.extraExpenses.set(craftPersistence.extraExpenses);
            craft.extraIncome.set(craftPersistence.extraIncome);
            craft.ignoreJournals.set(craftPersistence.ignoreJournals ?? false);

            // Set craft location if provided
            if (craftPersistence.craftLocationId) {
              const location = locations.find((l) => l.index === craftPersistence.craftLocationId);
              craft.craftLocation.set(location);
            }

            // Set consumables
            if (craftPersistence.consumables && craftPersistence.consumables.length > 0) {
              for (const consumable of craftPersistence.consumables) {
                const item = jsonReaderService.getItemFromIndex(consumable.uniqueName);
                if (item) {
                  craft.addConsumable(item, consumable.amount);
                }
              }
            }
          }
        }
      }
    }

    // Apply user prices
    for (const userPrice of persistenceModel.userPrices) {
      if (userPrice.type === 'input') {
        const inputItem = craftGroup.inputItems().find((item) => item.item.uniqueName === userPrice.itemUniqueName);
        if (inputItem) {
          if (userPrice.buyUserPrice !== undefined) {
            inputItem.buyUserPrice.set(userPrice.buyUserPrice);
          }
          if (userPrice.sellUserPrice !== undefined) {
            inputItem.sellUserPrice.set(userPrice.sellUserPrice);
          }
          if (userPrice.buyLocationId) {
            const location = locations.find((l) => l.index === userPrice.buyLocationId);
            inputItem.buyLocation.set(location);
          }
          if (userPrice.buyPriceType) {
            const priceType = priceTypes.find((pt) => pt.name === userPrice.buyPriceType);
            inputItem.buyPriceType.set(priceType);
          }
          if (userPrice.leftOverSellPriceType) {
            const priceType = priceTypes.find((pt) => pt.name === userPrice.leftOverSellPriceType);
            inputItem.leftOverSellPriceType.set(priceType);
          }
        }
      } else if (userPrice.type === 'output') {
        const outputItem = craftGroup.outputItems().find((item) => item.item.uniqueName === userPrice.itemUniqueName);
        if (outputItem) {
          if (userPrice.sellUserPrice !== undefined) {
            outputItem.sellUserPrice.set(userPrice.sellUserPrice);
          }
          if (userPrice.sellLocationId) {
            const location = locations.find((l) => l.index === userPrice.sellLocationId);
            outputItem.sellLocation.set(location);
          }
          if (userPrice.sellPriceType) {
            const priceType = priceTypes.find((pt) => pt.name === userPrice.sellPriceType);
            outputItem.sellPriceType.set(priceType);
          }
        }
      }
    }

    return craftGroup;
  }
}

// MARK: toDoneCraftPersistenceModel
export const toDoneCraftPersistenceModel = (craftGroup: CraftGroup): DoneCraftPersistanceModel => {
  return {
    craftGroup: CraftGroupPersistenceHelper.toPersistenceModel(craftGroup),
    totalFocus: craftGroup.totalFocus(),
    totalCraftFamePlayerTotal: craftGroup.totalCraftFamePlayerTotal(),
    totalCraftFee: craftGroup.totalCraftFee(),
    totalExpenses: craftGroup.totalExpenses(),
    totalIncome: craftGroup.totalIncome(),
    totalResult: craftGroup.totalResult(),
    totalInputWeight: craftGroup.totalInputWeight(),
    totalOutputWeight: craftGroup.totalOutputWeight(),
  };
};

// MARK: fromDoneCraftPersistenceModel
export const fromDoneCraftPersistenceModel = (
  toDoneCraftPersistenceModel: DoneCraftPersistanceModel,
  jsonReaderService: JsonReaderService,
  aoDataGetterService: AoDataGetterService,
  achievementsSignal: Signal<Achievement[]>,
  craftCalculatorStateService: CraftCalculatorStateService,
  locations: Location[],
  priceTypes: PriceType[]
): DoneCraft => {
  const craftGroup = CraftGroupPersistenceHelper.fromPersistenceModel(toDoneCraftPersistenceModel.craftGroup, jsonReaderService, aoDataGetterService, achievementsSignal, craftCalculatorStateService, locations, priceTypes);

  return new DoneCraft(
    toDoneCraftPersistenceModel.id,
    craftGroup,
    toDoneCraftPersistenceModel.totalFocus,
    toDoneCraftPersistenceModel.totalCraftFamePlayerTotal,
    toDoneCraftPersistenceModel.totalCraftFee,
    toDoneCraftPersistenceModel.totalExpenses,
    toDoneCraftPersistenceModel.totalIncome,
    toDoneCraftPersistenceModel.totalResult,
    toDoneCraftPersistenceModel.totalInputWeight,
    toDoneCraftPersistenceModel.totalOutputWeight,
    toDoneCraftPersistenceModel.craftDate
  );
};
